import * as THREE from "three";
import { createRusticWoodTexture, createWoodBumpTexture } from "./textures";
import type { Color, PieceType } from "../game/types";

/** Hauteur totale approximative (unites monde) de chaque type de piece, socle inclus. */
export const PIECE_HEIGHTS: Record<PieceType, number> = {
  p: 0.78,
  n: 0.55,
  b: 0.87,
  r: 0.56,
  q: 0.85,
  k: 0.95,
};

let cachedMaterials: { white: THREE.Material; black: THREE.Material } | null = null;

/**
 * Pieces en bois sculpte, pas en ivoire poli : grain marque, noeuds, finition
 * matte (roughness elevee, aucun metalness) pour un rendu rustique, "taille a
 * la main" plutot que manufacture. Bois clair (erable/buis) pour les Blancs,
 * bois fonce (noyer/chene fume) pour les Noirs — meme famille de materiau,
 * juste une essence differente, comme un vrai jeu d'echecs artisanal.
 */
function getMaterials() {
  if (cachedMaterials) return cachedMaterials;
  const bump = createWoodBumpTexture();

  const lightWood = createRusticWoodTexture("#d9b06a", "#a06f34", "#6b4423");
  const white = new THREE.MeshStandardMaterial({
    map: lightWood,
    bumpMap: bump,
    bumpScale: 0.012,
    roughness: 0.82,
    metalness: 0,
  });

  // Couleurs eclaircies et roughness abaissee par rapport aux Blancs (0.78 -> 0.58) :
  // en camera plongeante, avec l'eclairage tamise de la taverne, l'ancien coloris
  // (proche du noir pur) se noyait dans l'ombre du plateau — surtout la rangee la
  // plus eloignee de la lumiere cle. Un peu plus clair et surtout plus glacant (les
  // reflets speculaires, pas seulement la lumiere diffuse, dessinent alors le relief
  // des pieces meme peu eclairees) sans pour autant lire comme "brun" plutot que "noir".
  const darkWood = createRusticWoodTexture("#4a2e1c", "#2c1a0f", "#170d07");
  const black = new THREE.MeshStandardMaterial({
    map: darkWood,
    bumpMap: bump,
    bumpScale: 0.012,
    roughness: 0.58,
    metalness: 0,
  });

  cachedMaterials = { white, black };
  return cachedMaterials;
}

function lathePoints(points: readonly [number, number][]): THREE.Vector2[] {
  return points.map(([radius, y]) => new THREE.Vector2(radius, y));
}

/**
 * Cree une geometrie de revolution a partir d'un profil (rayon, hauteur).
 * IMPORTANT : THREE.LatheGeometry ne "bouche" jamais automatiquement les
 * extremites dont le rayon n'est pas nul (contrairement a un simple cone) —
 * un profil qui se termine a rayon > 0 laisse un trou beant au sommet,
 * visible comme un creux sombre vu de dessus (c'etait le bug sur la dame/
 * le roi/la tour). Toutes nos pieces doivent donc soit se refermer sur un
 * point (dernier rayon = 0), soit recevoir un capuchon plat explicite via
 * `flatCap()` ci-dessous.
 */
function buildLathe(points: readonly [number, number][], segments = 32): THREE.BufferGeometry {
  return new THREE.LatheGeometry(lathePoints(points), segments);
}

/** Disque plat qui bouche un profil de lathe termine a rayon non nul (ex: le plateau d'une tour). */
function flatCap(radius: number, y: number, segments = 32): THREE.Mesh {
  const cap = new THREE.Mesh(new THREE.CircleGeometry(radius, segments));
  cap.rotation.x = -Math.PI / 2;
  cap.position.y = y;
  return cap;
}

/** Socle commun (pied evase + collerette) partage par toutes les pieces "tournees". */
function baseProfile(): [number, number][] {
  return [
    [0, 0],
    [0.27, 0],
    // Paroi verticale (rayon constant) : un vrai pied, d'epaisseur nettement visible,
    // plutot qu'un simple conge qui enchainait directement sur le fut.
    [0.27, 0.07],
    [0.23, 0.085],
    [0.2, 0.1],
  ];
}

/** Pion : silhouette Staunton classique — pied, fut galbe, cou fin, tete ronde. */
function pawnGeometry(): THREE.Group {
  // Hauteur augmentee directement dans le profil (valeurs Y), pas via une mise a
  // l'echelle globale : le rayon (silhouette en largeur) reste inchange, seule la
  // piece elle-meme est plus haute, comme un vrai modele different plutot qu'un
  // pion normal agrandi uniformement.
  const points: [number, number][] = [
    ...baseProfile(),
    [0.14, 0.22],
    [0.155, 0.34],
    [0.1, 0.5],
    [0.085, 0.56],
  ];
  const body = new THREE.Mesh(buildLathe(points));
  const group = new THREE.Group();
  group.add(body);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.12, 20, 16));
  head.position.y = 0.66;
  group.add(head);
  return group;
}

/** Tour : fut cylindrique, plateau plat rebouche explicitement, creneaux. */
function rookGeometry(): THREE.Group {
  const topY = 0.5;
  const topRadius = 0.26;
  const points: [number, number][] = [
    ...baseProfile(),
    [0.2, 0.12],
    [0.2, 0.42],
    [topRadius, 0.45],
    [topRadius, topY],
  ];
  const body = new THREE.Mesh(buildLathe(points));
  const group = new THREE.Group();
  group.add(body);
  group.add(flatCap(topRadius, topY));

  // Chaque merlon est tourne pour presenter une face plate vers l'exterieur
  // (alignee sur l'arrete circulaire du plateau) plutot qu'un coin en biais :
  // sans cette rotation, seuls les merlons a 0/90/180/270 tombaient juste,
  // les autres presentaient une pointe de diamant vers le bord.
  const crenellationCount = 8;
  const radius = 0.19;
  for (let i = 0; i < crenellationCount; i++) {
    const angle = (i / crenellationCount) * Math.PI * 2;
    const merlon = new THREE.Mesh(new THREE.BoxGeometry(0.085, 0.07, 0.085));
    merlon.position.set(Math.cos(angle) * radius, topY + 0.035, Math.sin(angle) * radius);
    merlon.rotation.y = -angle;
    group.add(merlon);
  }
  return group;
}

/**
 * Fou : fut elance, large collerette evasee, mitre ronde a fente sommitale et
 * petite pointe de faitage. Le jeu a une camera plongeante quasi verticale :
 * une simple boule sur un fut (silhouette vue de dessus quasi identique a
 * celle du pion) ne suffit pas a s'en distinguer. La collerette large (bien
 * plus qu'un simple filet) et la pointe sommitale sont ce qui casse le plus
 * nettement ce contour vu de haut ; le pion n'a ni l'une ni l'autre.
 */
function bishopGeometry(): THREE.Group {
  const points: [number, number][] = [
    ...baseProfile(),
    [0.16, 0.16],
    [0.19, 0.3],
    [0.11, 0.46],
    [0.095, 0.52],
  ];
  const body = new THREE.Mesh(buildLathe(points));
  const group = new THREE.Group();
  group.add(body);

  // Collerette large et plate (un vrai disque evase, pas un simple filet) :
  // vue de dessus c'est elle qui rompt le plus nettement avec le fut lisse
  // du pion, qui n'a aucune rupture de diametre entre le corps et la tete.
  const collarPoints: [number, number][] = [
    [0.095, 0.52],
    [0.21, 0.535],
    [0.21, 0.555],
    [0.1, 0.565],
  ];
  const collar = new THREE.Mesh(buildLathe(collarPoints));
  group.add(collar);

  // Mitre : deux demi-spheres separees par un veritable interstice, plutot
  // qu'une boite plaquee par-dessus une sphere pleine (l'ancienne fente
  // debordait de la silhouette ronde des que sa largeur approchait le rayon
  // de la tete — le bug visuel signale). L'interstice recoit une vraie ombre
  // portee par l'eclairage de la scene, renforcee par un disque tres sombre
  // qui bouche chaque moitie (visible meme sous un eclairage plat).
  const headRadius = 0.135;
  const slitGap = 0.024;
  const slitMaterial = new THREE.MeshStandardMaterial({ color: "#1a0f06", roughness: 0.9 });

  const mitre = new THREE.Group();

  const topHalf = new THREE.Mesh(
    new THREE.SphereGeometry(headRadius, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2),
  );
  topHalf.position.y = slitGap / 2;
  mitre.add(topHalf);

  const topCap = new THREE.Mesh(new THREE.CircleGeometry(headRadius, 20), slitMaterial);
  topCap.rotation.x = Math.PI / 2;
  topCap.position.y = slitGap / 2;
  topCap.userData.customMaterial = true;
  mitre.add(topCap);

  const bottomHalf = new THREE.Mesh(
    new THREE.SphereGeometry(headRadius, 20, 10, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2),
  );
  bottomHalf.position.y = -slitGap / 2;
  mitre.add(bottomHalf);

  const bottomCap = new THREE.Mesh(new THREE.CircleGeometry(headRadius, 20), slitMaterial);
  bottomCap.rotation.x = -Math.PI / 2;
  bottomCap.position.y = -slitGap / 2;
  bottomCap.userData.customMaterial = true;
  mitre.add(bottomCap);

  const mitreY = 0.665;
  mitre.position.y = mitreY;
  mitre.rotation.z = Math.PI / 9;
  group.add(mitre);

  // Pointe de faitage : petit repere au sommet qui casse le contour rond vu
  // de dessus et signe la mitre, contrairement a la tete lisse et nue du pion.
  const finial = new THREE.Mesh(new THREE.SphereGeometry(0.032, 12, 10));
  finial.position.y = mitreY + slitGap / 2 + headRadius + 0.028;
  group.add(finial);

  return group;
}

/** Dame : fut galbe se refermant en pointe (fixe le trou beant de l'ancienne version), couronne a pointes. */
function queenGeometry(): THREE.Group {
  const points: [number, number][] = [
    ...baseProfile(),
    [0.17, 0.16],
    [0.21, 0.3],
    [0.12, 0.48],
    [0.19, 0.62],
    [0.21, 0.66],
    [0, 0.7],
  ];
  const body = new THREE.Mesh(buildLathe(points));
  const group = new THREE.Group();
  group.add(body);

  const spikeCount = 7;
  const radius = 0.15;
  for (let i = 0; i < spikeCount; i++) {
    const angle = (i / spikeCount) * Math.PI * 2;
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.15, 8));
    spike.position.set(Math.cos(angle) * radius, 0.66, Math.sin(angle) * radius);
    group.add(spike);
  }

  const finial = new THREE.Mesh(new THREE.SphereGeometry(0.06, 16, 12));
  finial.position.y = 0.79;
  group.add(finial);
  return group;
}

/** Roi : le plus haut fut, se refermant en pointe (idem dame), croix sommitale. */
function kingGeometry(): THREE.Group {
  const points: [number, number][] = [
    ...baseProfile(),
    [0.18, 0.18],
    [0.24, 0.34],
    [0.14, 0.56],
    [0.22, 0.72],
    [0.24, 0.76],
    [0, 0.79],
  ];
  const body = new THREE.Mesh(buildLathe(points));
  const group = new THREE.Group();
  group.add(body);

  const collar = new THREE.Mesh(new THREE.TorusGeometry(0.19, 0.025, 10, 24));
  collar.rotation.x = Math.PI / 2;
  collar.position.y = 0.76;
  group.add(collar);

  const cross = new THREE.Group();
  const vertical = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.24, 0.045));
  vertical.position.y = 0.93;
  const horizontal = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.05, 0.045));
  horizontal.position.y = 0.89;
  cross.add(vertical, horizontal);
  group.add(cross);
  return group;
}

/**
 * Tete de cheval : profil lateral extrude (seule piece non a symetrie de
 * revolution) auquel s'ajoutent museau, oreilles, criniere et oeil en relief.
 * Le seul profil extrude a plat, meme bien decoupe, rend a plat comme un
 * jeton decoupe plutot que comme du bois sculpte, surtout vu de la camera
 * plongeante du jeu ; ces volumes en saillie cassent l'aplat et lisent comme
 * un cheval, pas comme un chevron abstrait.
 *
 * Tout l'assemblage est construit dans le repere du profil d'origine (x =
 * museau <-> encolure, y = hauteur, z = epaisseur) puis pivote d'un bloc a la
 * fin : recalculer chaque position apres coup serait fastidieux, la rotation
 * de 90 deg autour de Y echangeant x et z.
 */
function knightGeometry(): THREE.Group {
  const shape = new THREE.Shape();
  shape.moveTo(-0.14, 0.14);
  shape.lineTo(-0.16, 0.4);
  shape.quadraticCurveTo(-0.17, 0.56, -0.05, 0.62);
  shape.lineTo(0.14, 0.7);
  shape.quadraticCurveTo(0.24, 0.73, 0.24, 0.65);
  shape.quadraticCurveTo(0.24, 0.6, 0.16, 0.58);
  shape.lineTo(0.02, 0.5);
  shape.quadraticCurveTo(0.16, 0.46, 0.2, 0.34);
  shape.quadraticCurveTo(0.22, 0.26, 0.14, 0.22);
  shape.lineTo(0.08, 0.3);
  shape.quadraticCurveTo(0.02, 0.24, -0.02, 0.16);
  shape.lineTo(-0.14, 0.14);

  const depth = 0.15;
  const extrude = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSize: 0.03,
    bevelThickness: 0.026,
    bevelSegments: 4,
  });

  const raw = new THREE.Group();

  const head = new THREE.Mesh(extrude);
  head.position.z = -depth / 2;
  raw.add(head);

  // Museau : renflement arrondi a la pointe du nez, la ou le profil seul est
  // le plus fin.
  const muzzle = new THREE.Mesh(new THREE.SphereGeometry(0.062, 14, 10));
  muzzle.scale.set(0.8, 0.65, 1.1);
  muzzle.position.set(-0.155, 0.23, 0);
  raw.add(muzzle);

  // Oreilles : deux cones aplatis pointant vers l'avant-haut du crane.
  const earGeometry = new THREE.ConeGeometry(0.026, 0.09, 8);
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(earGeometry);
    ear.position.set(0.15, 0.77, side * 0.045);
    ear.rotation.set(0, 0, -0.4);
    raw.add(ear);
  }

  // Criniere : petites aretes en rang le long de l'encolure (poll -> garrot),
  // comme des coups de gouge, pour eviter l'aplat entre les deux courbes.
  const maneCount = 5;
  for (let i = 0; i < maneCount; i++) {
    const t = i / (maneCount - 1);
    const tuft = new THREE.Mesh(new THREE.ConeGeometry(0.026, 0.075, 6));
    tuft.position.set(0.045 + t * 0.14, 0.35 + t * 0.24, 0);
    tuft.rotation.z = -1.0;
    raw.add(tuft);
  }

  // Oeil : petit point sombre pour ancrer le regard, detail de sculpteur.
  const eyeMaterial = new THREE.MeshStandardMaterial({ color: "#1a0f06", roughness: 0.9 });
  const eye = new THREE.Mesh(new THREE.SphereGeometry(0.013, 8, 6), eyeMaterial);
  eye.position.set(-0.1, 0.43, 0.078);
  eye.userData.customMaterial = true;
  raw.add(eye);

  raw.rotation.y = Math.PI / 2;

  const stemPoints: [number, number][] = [...baseProfile(), [0.17, 0.13], [0, 0.16]];
  const stem = new THREE.Mesh(buildLathe(stemPoints));

  const group = new THREE.Group();
  group.add(stem, raw);
  return group;
}

/** Cree l'entite 3D (Group) d'une piece, materiaux poses, prete a etre positionnee sur l'echiquier. */
export function createPieceMesh(type: PieceType, color: Color): THREE.Group {
  const materials = getMaterials();
  const material = color === "w" ? materials.white : materials.black;

  let group: THREE.Group;
  switch (type) {
    case "p":
      group = pawnGeometry();
      break;
    case "r":
      group = rookGeometry();
      break;
    case "b":
      group = bishopGeometry();
      break;
    case "n":
      group = knightGeometry();
      break;
    case "q":
      group = queenGeometry();
      break;
    case "k":
      group = kingGeometry();
      break;
  }

  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      // La fente de la mitre du fou porte deja son propre materiau sombre
      // contrastant (cf. `bishopGeometry`) : ne pas l'ecraser par le bois commun.
      if (!child.userData.customMaterial) {
        child.material = material;
      }
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
  group.userData.pieceType = type;
  group.userData.pieceColor = color;
  return group;
}
